import { writeHandoff } from './handoff';

type Gtag = { gtag?: (...args: unknown[]) => void; clarity?: (...args: unknown[]) => void };

/** A click through to a partner: the GA4 conversion event, and the same mark on the Clarity
 *  session (its recordings can then be filtered to visitors who clicked through, and
 *  "upgrade" keeps those recordings). The click itself is counted server-side in the /go/
 *  redirect (ad-block-proof, no double counting); this never blocks it.
 *
 *  It also leaves the hand-off record (src/lib/handoff.ts) for the /out/ card, so the card
 *  repeats the price and party size this visitor saw on the board. */
export const trackOutbound = (eventId: string, market: string, placement?: string) => {
  writeHandoff(eventId, market, placement);
  try {
    const w = window as unknown as Gtag;
    w.gtag?.('event', 'marketplace_click', {
      marketplace: market,
      event_id: eventId,
      ...(placement ? { placement } : {}),
    });
    w.clarity?.('event', 'marketplace_click');
    w.clarity?.('set', 'marketplace', market);
    w.clarity?.('upgrade', 'marketplace_click');
  } catch {
    /* never block the click */
  }
};

/** The settled-search counter (functions/api/search-stat.ts, cookieless, no identifier). A beacon:
 *  it never delays a navigation and the answer is never read. */
const searchBeacon = (body: Record<string, unknown>) => {
  try {
    const data = JSON.stringify(body);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) navigator.sendBeacon('/api/search-stat', data);
    else void fetch('/api/search-stat', { method: 'POST', body: data, keepalive: true }).catch(() => {});
  } catch {
    /* counting never gets in the way */
  }
};
/** query → whether the SUBMITTED answer was the one counted (a box's type-ahead answer first, then Enter) */
const settledSearches = new Map<string, boolean>();

/** A search that SETTLED — its remote answer stood unchanged for a moment, or the visitor submitted
 *  it — counted once per query per page view, with how many results it found (null: the help card)
 *  and its top hub ('kind:slug'). Never for a half-typed prefix, and never when the local fallback
 *  answered (the caller knows). `submitted`: the answer is the one Enter asked for (a submitted
 *  search is guarded: "the" is nothing there, but had rows as typed ahead): when the type-ahead
 *  answer of the same query was counted first, the submitted one replaces its results and top hub
 *  (`replace`) and counts nothing more. */
export const trackSearch = (q: string, results: number | null, hub?: string | null, submitted = false) => {
  const key = q.trim().toLowerCase();
  if (key.length < 2) return;
  const had = settledSearches.get(key);
  if (had === true || (had === false && !submitted)) return;
  settledSearches.set(key, submitted);
  searchBeacon({ q: key, n: results, hub: hub ?? '', tap: 0, ...(had === false ? { replace: 1 } : {}) });
};

/** A tap on a search result (a hub, an event, the best match, a suggestion). */
export const trackSearchTap = (q: string, hub?: string | null) => {
  const key = q.trim().toLowerCase();
  if (key.length >= 2) searchBeacon({ q: key, hub: hub ?? '', tap: 1 });
};

/** A tap from a buyer guide (src/pages/guides/) to another of our pages: an event search, the price
 *  research, another guide. GA4 only, no partner click (those stay trackOutbound's, from the board):
 *  the guides send people to our comparison pages, never straight to a marketplace. */
export const trackGuideClick = (guide: string, destination: string) => {
  try {
    (window as unknown as Gtag).gtag?.('event', 'guide_click', { guide_slug: guide, destination });
  } catch {
    /* never block */
  }
};

/** The retention features' GA4 events, named once here; each call site lands with its feature. */
export type RetentionEvent = 'save_event' | 'unsave_event' | 'follow_hub' | 'recent_open' | 'calendar_add' | 'share' | 'welcome_back_click';
export const trackRetention = (name: RetentionEvent, params: Record<string, string | number> = {}) => {
  try {
    (window as unknown as Gtag).gtag?.('event', name, params);
  } catch {
    /* never block */
  }
};
